var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { BlockchainTransactionDto, BlockchainTransactionStatus } from "healthex-common/blockchain.dtos";
import { asEnum } from "healthex-common/utils";
import { action, observable, reaction, when } from "mobx";
export var BlockchainTransactionState;
(function (BlockchainTransactionState) {
    BlockchainTransactionState["WAITING_TO_CONFIRM"] = "WAITING_TO_CONFIRM";
    BlockchainTransactionState["CONFIRMING"] = "CONFIRMING";
    BlockchainTransactionState["CONFIRMED"] = "CONFIRMED";
    BlockchainTransactionState["DENIED"] = "DENIED";
    BlockchainTransactionState["EXPIRED"] = "EXPIRED";
})(BlockchainTransactionState || (BlockchainTransactionState = {}));
const statusToState = {
    [BlockchainTransactionStatus.PENDING]: BlockchainTransactionState.WAITING_TO_CONFIRM,
    [BlockchainTransactionStatus.NOTIFICATION_RECEIVED]: BlockchainTransactionState.WAITING_TO_CONFIRM,
    [BlockchainTransactionStatus.SENDING_TRANSACTION]: BlockchainTransactionState.CONFIRMING,
    [BlockchainTransactionStatus.MINING_TRANSACTION]: BlockchainTransactionState.CONFIRMING,
    [BlockchainTransactionStatus.SYNCING_BACKEND]: BlockchainTransactionState.CONFIRMING,
    [BlockchainTransactionStatus.DECLINED]: BlockchainTransactionState.DENIED,
    [BlockchainTransactionStatus.EXPIRED]: BlockchainTransactionState.EXPIRED,
    [BlockchainTransactionStatus.COMPLETED]: BlockchainTransactionState.CONFIRMED,
    [BlockchainTransactionStatus.FAILED]: BlockchainTransactionState.EXPIRED,
};
const stateIsComplete = {
    [BlockchainTransactionState.WAITING_TO_CONFIRM]: false,
    [BlockchainTransactionState.CONFIRMING]: false,
    [BlockchainTransactionState.CONFIRMED]: true,
    [BlockchainTransactionState.DENIED]: true,
    [BlockchainTransactionState.EXPIRED]: true,
};
export class BlockchainTransactionManager {
    constructor(authManager, blockchainService) {
        this.authManager = authManager;
        this.blockchainService = blockchainService;
        reaction(() => this.transaction, async () => {
            if (this.transaction) {
                console.debug(`Transaction update: ${this.transaction.state}`);
                if (!stateIsComplete[this.transaction.state]) {
                    console.debug(`Transaction update not complete: ${this.transaction.state}`);
                    await this.waitForStatusUpdate();
                }
            }
        });
    }
    async waitOnTransaction(transaction) {
        this.setTransaction(transaction);
        await when(() => !this.transaction);
    }
    setTransaction(transaction) {
        var _a, _b;
        console.log(`[Blockchain Transaction] Check ${typeof transaction.currentStatus} => ${transaction.currentStatus}`);
        const updatedStatus = asEnum(BlockchainTransactionStatus)((_a = transaction.currentStatus) !== null && _a !== void 0 ? _a : "");
        const updatedState = statusToState[updatedStatus !== null && updatedStatus !== void 0 ? updatedStatus : BlockchainTransactionStatus.PENDING];
        console.log(`[Blockchain Transaction] Replacing? ${(_b = this.transaction) === null || _b === void 0 ? void 0 : _b.state} => ${updatedState}/[${updatedStatus}]`);
        const updatedTransaction = {
            id: transaction.id,
            requestType: transaction.metadata.request,
            expiry: transaction.metadata.expiresAt,
            state: updatedState,
        };
        console.debug(`[Blockchain Transaction] Update: ${JSON.stringify(updatedTransaction)}`);
        this.transaction = updatedTransaction;
    }
    clearTransaction() {
        this.transaction = undefined;
    }
    async resendTransaction() {
        var _a;
        if (!this.authManager.id || !this.authManager.orgId) {
            console.log(`[Blockchain Transaction] No user to resend transaction`);
            return;
        }
        if (!((_a = this.transaction) === null || _a === void 0 ? void 0 : _a.id)) {
            console.log(`[Blockchain Transaction] No transaction to resend`);
            return;
        }
        try {
            const resentTransaction = await this.blockchainService.resend(this.authManager.id, this.authManager.orgId, this.transaction.id);
            this.setTransaction(resentTransaction);
        }
        catch (error) {
            console.error(`Error resending transaction`, error);
            this.clearTransaction();
        }
    }
    async waitForStatusUpdate() {
        if (!this.transaction) {
            console.log(`[Blockchain Transaction] No transaction to wait for`);
            return;
        }
        if (!this.authManager.orgId || !this.authManager.id) {
            console.log(`[Blockchain Transaction] No user waiting for transaction`);
            return;
        }
        console.debug(`[Blockchain Transaction] Waiting for update for ${this.transaction.id}. Currently [${this.transaction.state}]`);
        const updatedTransaction = await this.blockchainService.waitForStatusUpdate(this.authManager.id, this.authManager.orgId, this.transaction.id);
        if (updatedTransaction === undefined) {
            console.debug(`Transaction update: Null response`);
            this.clearTransaction();
            return;
        }
        this.setTransaction(updatedTransaction);
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], BlockchainTransactionManager.prototype, "transaction", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [BlockchainTransactionDto]),
    __metadata("design:returntype", Promise)
], BlockchainTransactionManager.prototype, "waitOnTransaction", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [BlockchainTransactionDto]),
    __metadata("design:returntype", void 0)
], BlockchainTransactionManager.prototype, "setTransaction", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], BlockchainTransactionManager.prototype, "clearTransaction", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], BlockchainTransactionManager.prototype, "resendTransaction", null);
